/**
 * Hydra Plugin
 *
 * Provides Hydra live-coding visuals functionality.
 * Registers the 'hydra' endpoint type and all Hydra nodes.
 */

// Register hydra data type
declare module '@nodes/Endpoint' {
  interface DataTypeRegistry {
    hydra: 'hydra';
  }
}

import type { Plugin, EndpointWithContext } from '@plugins/Plugin';
import { Domain } from '@nodes/Endpoint';
import { isBrowser } from '@runtime/env';
import { HydraEndpoint } from './HydraEndpoint';
import { sourceNodes } from './nodes/sources';
import { effectNodes } from './nodes/effects';
import { compositeNodes } from './nodes/composites';
import { modulateNodes } from './nodes/modulates';
import { hydraCanvasNodeDef } from './nodes/HydraCanvasNode';


// Hydra class - loaded on register
let HydraClass: any = null;

export const hydraPlugin: Plugin = {
  id: 'hydra',
  name: 'Hydra',
  namespace: 'hydra',
  domain: Domain.Video,

  // All Hydra nodes
  nodes: [
    hydraCanvasNodeDef,
    ...sourceNodes,
    ...effectNodes,
    ...compositeNodes,
    ...modulateNodes
  ],

  // Register 'hydra' endpoint type
  endpointDataTypes: ['hydra'],

  async onRegister() {
    // Only load in browser - hydra-synth attaches DOM listeners on import
    if (!isBrowser) return;

    // Hydra expects Node.js global
    if (typeof global === 'undefined') {
      (window as any).global = window;
    }

    try {
      const HydraModule = await import('hydra-synth');
      HydraClass = HydraModule.default || HydraModule;
    } catch (err) {
      console.error('Failed to load hydra-synth:', err);
    }
  },

  createNode(type: string, id: string, deps: any) {
    const nodeDef = this.nodes!.find(n => n.type === type);
    if (!nodeDef) {
      throw new Error(`Unknown Hydra node type: ${type}`);
    }
    // Pass HydraClass in deps
    return new nodeDef.ctor(id, deps);
  },

  createEndpoint(config: EndpointWithContext) {
    return new HydraEndpoint(config, this.store!, HydraClass);
  },

  async registerUI({ registerViz }) {
    // Hydra uses the same canvas visualization as video plugin
    const { CanvasVisualization } = await import('../video/ui/CanvasVisualization');
    registerViz('Canvas', CanvasVisualization);
  }
};
